import { _isNotBlank, _notNil } from '@/littledash';
import { modalAction } from '@/utils/modal';
import cn from 'classnames';
import Spinner from '@/components/UI/Spinner';
import { type CSSProperties, type FC, type ReactNode, useState } from 'react';
import { useDispatch } from 'react-redux';
import styles from './TimedDeleteConfirmation.module.scss';

interface TimedDeleteConfirmationProps {
  title: string;
  text?: string;
  render?: () => ReactNode;
  btnTxt: string;
  btnColor?: 'red' | 'dark-blue';
  cancelBtnTxt?: string;
  delayDuration?: `${number}${'s' | 'ms'}`;
  onClick: () => Promise<void>;
  onCancel?: () => Promise<void> | void;
  onClickClose?: boolean;
  onCancelClose?: boolean;
  showLoadingOnClick?: boolean;
}

export const TimedDeleteConfirmation: FC<TimedDeleteConfirmationProps> = ({
  title,
  btnTxt,
  btnColor = 'red',
  cancelBtnTxt = 'Cancel',
  delayDuration,
  text,
  render,
  onClick,
  onCancel,
  onClickClose = true,
  onCancelClose = true,
  showLoadingOnClick = false,
}) => {
  const [deleteDisabled, setDeleteDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const { closeModal } = modalAction(useDispatch());
  const handleClick = async () => {
    if (deleteDisabled || isLoading) {
      return;
    }
    if (showLoadingOnClick) {
      setIsLoading(true);
    }
    try {
      await onClick();
    } finally {
      if (showLoadingOnClick) {
        setIsLoading(false);
      }
    }
    if (onClickClose) {
      closeModal();
    }
  };

  return (
    <div className="center mv3 bg-white br2 shadow-4" style={{ maxWidth: '19rem' }}>
      <div className="pa3">
        <div className="pb3 tc">
          <h3 className="normal f4 pb2">{title}</h3>
          {_notNil(render) && _notNil(render()) ? <>{render()}</> : <p className="f6 pb2">{text}</p>}
        </div>
        <div>
          {isLoading ? (
            <div className="flex flex-column items-center pv3" data-testid="loading-spinner">
              <Spinner size="small" color="blue" />
            </div>
          ) : (
            <>
              <div
                className={cn(styles.deleteConfirmButton, {
                  [styles.red]: btnColor === 'red',
                  [styles.darkBlue]: btnColor === 'dark-blue',
                })}
                style={
                  _isNotBlank(delayDuration)
                    ? ({ '--confirmation-animation-duration': delayDuration } as CSSProperties)
                    : undefined
                }
                onAnimationEnd={() => setDeleteDisabled(false)}
                aria-disabled={deleteDisabled}
                onClick={handleClick}
              >
                <div className={styles.content}>{btnTxt}</div>
                <div className={styles.progress}></div>
              </div>
              <a
                className="button plain db w-100 mt2"
                onClick={() => {
                  if (onCancelClose) {
                    closeModal();
                  }
                  onCancel?.();
                }}
              >
                {cancelBtnTxt}
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
